import { Temporal } from 'temporal-polyfill';

const NANOSECONDS_PER_MILLISECOND = 1_000_000n;
const MILLISECONDS_PER_SECOND = 1_000n;
const MILLISECONDS_PER_DAY = 86_400_000n;
const MAX_EPOCH_MILLISECONDS = 8_640_000_000_000_000n;
const SPREADSHEET_BASE_DATE = Temporal.PlainDate.from('1899-12-31');
const SPREADSHEET_LEAP_BUG_END = Temporal.PlainDate.from('1900-03-01');
const DECIMAL_PATTERN = /^([+-]?)(\d+)(?:\.(\d+))?$/;
const ISO_INSTANT_PATTERN = /^(?:\d{4}|[+-]\d{6})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:[.,]\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/i;

/**
 * A user-correctable date entry error. `code` is stable enough for the UI to
 * select an appropriate message while `details` carries the rejected value.
 */
export class DateInputError extends Error {
  constructor(message, code = 'INVALID_DATE_INPUT', details = {}) {
    super(message);
    this.name = 'DateInputError';
    this.code = code;
    this.details = details;
  }
}

function inputError(message, code, details, cause) {
  const error = new DateInputError(message, code, details);
  if (cause !== undefined) {
    error.cause = cause;
  }
  return error;
}

function assertText(value, label, code) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw inputError(`${label} is required.`, code, { value });
  }
  return value.trim();
}

function floorDiv(value, divisor) {
  let quotient = value / divisor;
  if (value < 0n && value % divisor !== 0n) {
    quotient -= 1n;
  }
  return quotient;
}

function canonicalInstant(value, code = 'INVALID_INSTANT') {
  try {
    const instant = Temporal.Instant.from(value);
    const epochMilliseconds = floorDiv(
      instant.epochNanoseconds,
      NANOSECONDS_PER_MILLISECOND,
    );
    return instantFromEpochMilliseconds(epochMilliseconds);
  } catch (error) {
    if (error instanceof DateInputError) {
      throw error;
    }
    throw inputError('The instant is outside the supported date range.', code, { value }, error);
  }
}

function epochMillisecondsBigInt(instant) {
  return canonicalInstant(instant).epochNanoseconds / NANOSECONDS_PER_MILLISECOND;
}

function pad(value, length = 2) {
  return String(value).padStart(length, '0');
}

function formatPlainTime(zonedDateTime) {
  return `${pad(zonedDateTime.hour)}:${pad(zonedDateTime.minute)}:${pad(
    zonedDateTime.second,
  )}.${pad(zonedDateTime.millisecond, 3)}`;
}

function parseDecimal(text, label, code) {
  const normalized = assertText(text, label, code);
  const match = DECIMAL_PATTERN.exec(normalized);
  if (!match) {
    throw inputError(`${label} must be a plain decimal number.`, code, { value: text });
  }

  const [, signText, wholeText, fractionText = ''] = match;
  const scale = 10n ** BigInt(fractionText.length);
  const magnitude = BigInt(wholeText) * scale + BigInt(fractionText || '0');
  const numerator = signText === '-' ? -magnitude : magnitude;

  return { normalized, numerator, scale, fractionText };
}

function formatRationalDecimal(numerator, denominator, maximumFractionDigits) {
  if (numerator === 0n) {
    return '0';
  }

  const negative = numerator < 0n;
  let magnitude = negative ? -numerator : numerator;
  let whole = magnitude / denominator;
  const remainder = magnitude % denominator;

  if (remainder === 0n || maximumFractionDigits === 0) {
    return `${negative ? '-' : ''}${whole}`;
  }

  const factor = 10n ** BigInt(maximumFractionDigits);
  const scaledRemainder = remainder * factor;
  let fraction = scaledRemainder / denominator;
  const roundingRemainder = scaledRemainder % denominator;

  if (roundingRemainder * 2n >= denominator) {
    fraction += 1n;
  }
  if (fraction === factor) {
    whole += 1n;
    fraction = 0n;
  }

  const fractionText = fraction
    .toString()
    .padStart(maximumFractionDigits, '0')
    .replace(/0+$/, '');

  return `${negative ? '-' : ''}${whole}${fractionText ? `.${fractionText}` : ''}`;
}

function coercePlainDateTime(value) {
  try {
    const parsed = Temporal.PlainDateTime.from(value, { overflow: 'reject' });
    return parsed.with({ microsecond: 0, nanosecond: 0 });
  } catch (error) {
    throw inputError(
      'Enter a complete and valid local date and time.',
      'INVALID_LOCAL_DATE_TIME',
      { value },
      error,
    );
  }
}

/** Validate an IANA time-zone identifier and return Temporal's canonical ID. */
export function validateTimeZone(zone) {
  const candidate = assertText(zone, 'Time zone', 'INVALID_TIME_ZONE');

  try {
    return Temporal.Instant.fromEpochMilliseconds(0).toZonedDateTimeISO(candidate).timeZoneId;
  } catch (error) {
    throw inputError(
      `“${candidate}” is not a supported IANA time zone.`,
      'INVALID_TIME_ZONE',
      { zone: candidate },
      error,
    );
  }
}

/** Construct a millisecond-precision instant without passing through a Date. */
export function instantFromEpochMilliseconds(milliseconds) {
  let integer;

  try {
    if (typeof milliseconds === 'bigint') {
      integer = milliseconds;
    } else if (
      typeof milliseconds === 'number' &&
      Number.isFinite(milliseconds) &&
      Number.isSafeInteger(milliseconds)
    ) {
      integer = BigInt(milliseconds);
    } else if (typeof milliseconds === 'string' && /^[+-]?\d+$/.test(milliseconds.trim())) {
      integer = BigInt(milliseconds.trim());
    } else {
      throw new TypeError('Epoch milliseconds must be an integer.');
    }

    if (integer < -MAX_EPOCH_MILLISECONDS || integer > MAX_EPOCH_MILLISECONDS) {
      throw new RangeError('Epoch milliseconds are outside the Temporal range.');
    }

    return Temporal.Instant.fromEpochMilliseconds(Number(integer));
  } catch (error) {
    throw inputError(
      'Unix milliseconds must be a safe integer in the supported date range.',
      'INVALID_UNIX_MILLISECONDS',
      { value: milliseconds },
      error,
    );
  }
}

/** Parse signed Unix seconds, retaining at most millisecond precision. */
export function parseUnixSeconds(text) {
  const { numerator, scale } = parseDecimal(text, 'Unix seconds', 'INVALID_UNIX_SECONDS');
  const epochMilliseconds = (numerator * MILLISECONDS_PER_SECOND) / scale;

  try {
    return instantFromEpochMilliseconds(epochMilliseconds);
  } catch (error) {
    throw inputError(
      'Unix seconds are outside the supported date range.',
      'INVALID_UNIX_SECONDS',
      { value: text },
      error,
    );
  }
}

/** Format Unix seconds with no unnecessary zeroes and at most three decimals. */
export function formatUnixSeconds(instant) {
  return formatRationalDecimal(
    epochMillisecondsBigInt(instant),
    MILLISECONDS_PER_SECOND,
    3,
  );
}

/** Parse integer Unix milliseconds. */
export function parseUnixMilliseconds(text) {
  const normalized = assertText(text, 'Unix milliseconds', 'INVALID_UNIX_MILLISECONDS');
  if (!/^[+-]?\d+$/.test(normalized)) {
    throw inputError(
      'Unix milliseconds must be a whole number.',
      'INVALID_UNIX_MILLISECONDS',
      { value: text },
    );
  }
  return instantFromEpochMilliseconds(normalized);
}

/** Format Unix milliseconds as a base-10 integer. */
export function formatUnixMilliseconds(instant) {
  return epochMillisecondsBigInt(instant).toString();
}

/** Parse a strict ISO/RFC 3339 timestamp containing an explicit UTC offset. */
export function parseIsoInstant(text) {
  const normalized = assertText(text, 'ISO timestamp', 'INVALID_ISO');
  if (!ISO_INSTANT_PATTERN.test(normalized)) {
    throw inputError(
      'ISO timestamps must include a complete date, time, seconds, and UTC offset.',
      'INVALID_ISO',
      { value: text },
    );
  }

  try {
    return canonicalInstant(Temporal.Instant.from(normalized), 'INVALID_ISO');
  } catch (error) {
    if (error instanceof DateInputError && error.code === 'INVALID_ISO') {
      throw error;
    }
    throw inputError('The ISO timestamp is invalid.', 'INVALID_ISO', { value: text }, error);
  }
}

/** Format an instant as UTC ISO 8601 with an explicit three-digit millisecond field. */
export function formatIsoUtc(instant) {
  return canonicalInstant(instant).toString({ fractionalSecondDigits: 3 });
}

/** Format an instant as local ISO 8601 with its numeric zone offset, but no zone annotation. */
export function formatLocalIso(instant, zone) {
  const canonicalZone = validateTimeZone(zone);
  const zoned = canonicalInstant(instant).toZonedDateTimeISO(canonicalZone);
  const local = zoned.toPlainDateTime().toString({ fractionalSecondDigits: 3 });
  return `${local}${zoned.offset}`;
}

/**
 * Resolve a wall-clock value in a zone. Gaps are always rejected. In a fold,
 * callers may explicitly select `earlier` or `later`; `reject` asks the UI to
 * collect that choice instead.
 */
export function resolvePlainDateTime(plainDateTime, zone, disambiguation = 'earlier') {
  const canonicalZone = validateTimeZone(zone);
  const plain = coercePlainDateTime(plainDateTime);
  const requested = disambiguation ?? 'earlier';

  if (!['earlier', 'later', 'compatible', 'reject'].includes(requested)) {
    throw inputError(
      'DST disambiguation must be earlier, later, compatible, or reject.',
      'INVALID_DISAMBIGUATION',
      { disambiguation },
    );
  }

  let earlier;
  let later;
  try {
    earlier = plain.toZonedDateTime(canonicalZone, { disambiguation: 'earlier' });
    later = plain.toZonedDateTime(canonicalZone, { disambiguation: 'later' });
  } catch (error) {
    throw inputError(
      'The local date and time cannot be resolved in this time zone.',
      'INVALID_LOCAL_DATE_TIME',
      { plainDateTime: plain.toString(), zone: canonicalZone },
      error,
    );
  }

  const earlierMatches = earlier.toPlainDateTime().equals(plain);
  const laterMatches = later.toPlainDateTime().equals(plain);

  if (!earlierMatches && !laterMatches) {
    throw inputError(
      `${plain.toString()} does not exist in ${canonicalZone} because the clocks move forward.`,
      'NONEXISTENT_LOCAL_TIME',
      { plainDateTime: plain.toString(), zone: canonicalZone },
    );
  }

  const earlierInstant = canonicalInstant(earlier.toInstant());
  const laterInstant = canonicalInstant(later.toInstant());
  const isFold = !earlierInstant.equals(laterInstant);

  if (!isFold) {
    return { instant: earlierInstant, fold: 'none' };
  }

  const alternatives = { earlier: earlierInstant, later: laterInstant };
  if (requested === 'reject') {
    throw inputError(
      `${plain.toString()} occurs twice in ${canonicalZone}; choose the earlier or later occurrence.`,
      'AMBIGUOUS_LOCAL_TIME',
      { plainDateTime: plain.toString(), zone: canonicalZone, alternatives },
    );
  }

  const useLater = requested === 'later';
  return {
    instant: useLater ? laterInstant : earlierInstant,
    fold: useLater ? 'later' : 'earlier',
    alternatives,
  };
}

/** Parse an Excel/Google Sheets 1900-system serial as selected-zone wall time. */
export function parseSpreadsheet(text, zone, disambiguation = 'earlier') {
  const { numerator, scale } = parseDecimal(
    text,
    'Spreadsheet serial',
    'INVALID_SPREADSHEET',
  );

  const serialDay = floorDiv(numerator, scale);
  const fractionalNumerator = numerator - serialDay * scale;

  // Excel's serial 60 represents the fictitious 1900-02-29. Every time on
  // that fictitious day is non-existent, not merely the midnight value.
  if (serialDay === 60n) {
    throw inputError(
      'Spreadsheet serial 60 is Excel’s fictitious February 29, 1900.',
      'SPREADSHEET_SERIAL_60',
      { value: text },
    );
  }

  let millisecondsOfDay =
    (fractionalNumerator * MILLISECONDS_PER_DAY + scale / 2n) / scale;
  let actualDay = serialDay > 60n ? serialDay - 1n : serialDay;

  if (millisecondsOfDay === MILLISECONDS_PER_DAY) {
    millisecondsOfDay = 0n;
    actualDay += 1n;
  }

  if (
    actualDay < BigInt(Number.MIN_SAFE_INTEGER) ||
    actualDay > BigInt(Number.MAX_SAFE_INTEGER)
  ) {
    throw inputError(
      'Spreadsheet serial is outside the supported date range.',
      'INVALID_SPREADSHEET',
      { value: text },
    );
  }

  try {
    const date = SPREADSHEET_BASE_DATE.add({ days: Number(actualDay) });
    const hour = Number(millisecondsOfDay / 3_600_000n);
    const afterHour = millisecondsOfDay % 3_600_000n;
    const minute = Number(afterHour / 60_000n);
    const afterMinute = afterHour % 60_000n;
    const second = Number(afterMinute / 1_000n);
    const millisecond = Number(afterMinute % 1_000n);
    const plain = date.toPlainDateTime(
      new Temporal.PlainTime(hour, minute, second, millisecond),
    );

    return resolvePlainDateTime(plain, zone, disambiguation).instant;
  } catch (error) {
    if (error instanceof DateInputError) {
      throw error;
    }
    throw inputError(
      'Spreadsheet serial is outside the supported date range.',
      'INVALID_SPREADSHEET',
      { value: text },
      error,
    );
  }
}

/** Format selected-zone wall time as a 1900-system serial with at most 12 decimals. */
export function formatSpreadsheet(instant, zone) {
  const canonicalZone = validateTimeZone(zone);
  const zoned = canonicalInstant(instant).toZonedDateTimeISO(canonicalZone);
  const date = zoned.toPlainDate();
  const actualDays = BigInt(
    SPREADSHEET_BASE_DATE.until(date, { largestUnit: 'day' }).days,
  );
  const serialDays =
    Temporal.PlainDate.compare(date, SPREADSHEET_LEAP_BUG_END) >= 0
      ? actualDays + 1n
      : actualDays;
  const millisecondsOfDay = BigInt(
    ((zoned.hour * 60 + zoned.minute) * 60 + zoned.second) * 1_000 +
      zoned.millisecond,
  );
  const numerator = serialDays * MILLISECONDS_PER_DAY + millisecondsOfDay;

  return formatRationalDecimal(numerator, MILLISECONDS_PER_DAY, 12);
}

/** Return the concise zone/time metadata displayed in the instrument header. */
export function getZoneStatus(instant, zone) {
  const canonicalZone = validateTimeZone(zone);
  const canonical = canonicalInstant(instant);
  const zoned = canonical.toZonedDateTimeISO(canonicalZone);
  let abbreviation = zoned.offset;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: canonicalZone,
      timeZoneName: 'short',
      year: 'numeric',
    }).formatToParts(new Date(canonical.epochMilliseconds));
    abbreviation = parts.find((part) => part.type === 'timeZoneName')?.value ?? abbreviation;
  } catch {
    // Numeric offset is always accurate and is a suitable fallback for zones
    // whose historical dates are outside a particular Intl implementation.
  }

  return {
    timeZone: canonicalZone,
    offset: zoned.offset,
    abbreviation,
    time: formatPlainTime(zoned),
    iso: formatLocalIso(canonical, canonicalZone),
  };
}
